import React from 'react';
import { FiTriangle } from 'react-icons/fi';
import { Mafs, Coordinates, Plot, Point, Text, Polygon } from 'mafs';
import LessonTemplate from './components/LessonTemplate';
import QuantLessonPracticeSlot from './components/QuantLessonPracticeSlot';

// Visual Components
const TriangleBasicsVisual = () => (
  <div
    style={{
      padding: '1rem',
      background: 'white',
      borderRadius: '8px',
      border: '1px solid var(--liwaza-outline-variant)',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1rem',
        color: 'var(--liwaza-primary)',
      }}
    >
      Triangle Components
    </h4>
    <Mafs width={400} height={350} viewBox={{ x: [-1, 8], y: [-1, 7] }}>
      <Coordinates.Cartesian
        xAxis={{ lines: false, labels: false }}
        yAxis={{ lines: false, labels: false }}
      />

      {/* Triangle */}
      <Polygon
        points={[
          [1, 1],
          [7, 1],
          [4, 5],
        ]}
        color="#2563eb"
        fillOpacity={0.3}
        strokeWidth={3}
      />

      {/* Vertices */}
      <Point x={1} y={1} color="#ef4444" />
      <Point x={7} y={1} color="#ef4444" />
      <Point x={4} y={5} color="#ef4444" />

      {/* Vertex labels */}
      <Text x={0.7} y={0.6} color="#dc2626" size={16} weight="bold">
        A
      </Text>
      <Text x={7.2} y={0.6} color="#dc2626" size={16} weight="bold">
        B
      </Text>
      <Text x={3.7} y={5.3} color="#dc2626" size={16} weight="bold">
        C
      </Text>

      {/* Side labels */}
      <Text x={4} y={0.5} color="#059669" size={14} weight="bold">
        c
      </Text>
      <Text x={5.7} y={3} color="#059669" size={14} weight="bold">
        a
      </Text>
      <Text x={2.3} y={3} color="#059669" size={14} weight="bold">
        b
      </Text>

      {/* Angle labels */}
      <Text x={1.5} y={1.3} color="#d97706" size={13} weight="bold">
        ∠A
      </Text>
      <Text x={6.3} y={1.3} color="#d97706" size={13} weight="bold">
        ∠B
      </Text>
      <Text x={3.7} y={4.5} color="#d97706" size={13} weight="bold">
        ∠C
      </Text>

      {/* Properties */}
      <Text x={0.5} y={6.5} color="#1f2937" size={14} weight="bold">
        Triangle Properties:
      </Text>
      <Text x={0.5} y={6.1} color="#1f2937" size={12}>
        • Sum of angles = 180°
      </Text>
      <Text x={0.5} y={5.8} color="#1f2937" size={12}>
        • Sum of any two sides {'>'} third side
      </Text>
      <Text x={0.5} y={5.5} color="#1f2937" size={12}>
        • Largest angle opposite longest side
      </Text>
    </Mafs>
  </div>
);

const TriangleTypesVisual = () => (
  <div
    style={{
      padding: '1rem',
      background: 'white',
      borderRadius: '8px',
      border: '1px solid var(--liwaza-outline-variant)',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1rem',
        color: 'var(--liwaza-primary)',
      }}
    >
      Types of Triangles
    </h4>
    <Mafs width={500} height={350} viewBox={{ x: [-6, 6], y: [-4, 4] }}>
      <Coordinates.Cartesian />

      {/* Scalene Triangle */}
      <Polygon
        points={[
          [-5, -2],
          [-3, -2],
          [-4.5, 0.5],
        ]}
        color="#ef4444"
        fillOpacity={0.3}
        strokeWidth={2}
      />
      <Text x={-4} y={-2.8} color="#dc2626" size={14} weight="bold">
        Scalene
      </Text>
      <Text x={-4.3} y={-3.1} color="#1f2937" size={12}>
        All sides different
      </Text>

      {/* Isosceles Triangle */}
      <Polygon
        points={[
          [-1, -2],
          [1, -2],
          [0, 1],
        ]}
        color="#10b981"
        fillOpacity={0.3}
        strokeWidth={2}
      />
      <Text x={0} y={-2.8} color="#059669" size={14} weight="bold">
        Isosceles
      </Text>
      <Text x={-0.5} y={-3.1} color="#1f2937" size={12}>
        Two sides equal
      </Text>

      {/* Equilateral Triangle */}
      <Polygon
        points={[
          [3, -2],
          [5, -2],
          [4, -0.27],
        ]}
        color="#2563eb"
        fillOpacity={0.3}
        strokeWidth={2}
      />
      <Text x={4} y={-2.8} color="#1d4ed8" size={14} weight="bold">
        Equilateral
      </Text>
      <Text x={3.7} y={-3.1} color="#1f2937" size={12}>
        All sides equal
      </Text>

      {/* Right Triangle */}
      <Polygon
        points={[
          [-5, 2],
          [-3, 2],
          [-3, 3],
        ]}
        color="#f59e0b"
        fillOpacity={0.3}
        strokeWidth={2}
      />
      {/* Right angle marker */}
      <Polygon
        points={[
          [-3.2, 2],
          [-3, 2],
          [-3, 2.2],
        ]}
        color="#f59e0b"
        strokeWidth={1}
        fillOpacity={0}
      />
      <Text x={-4} y={1.2} color="#d97706" size={14} weight="bold">
        Right
      </Text>
      <Text x={-4.1} y={0.9} color="#1f2937" size={12}>
        One 90° angle
      </Text>

      {/* Acute Triangle */}
      <Polygon
        points={[
          [-1, 2],
          [1, 2],
          [0, 3.5],
        ]}
        color="#7c3aed"
        fillOpacity={0.3}
        strokeWidth={2}
      />
      <Text x={0} y={1.2} color="#6d28d9" size={14} weight="bold">
        Acute
      </Text>
      <Text x={-0.3} y={0.9} color="#1f2937" size={12}>
        All angles &lt; 90°
      </Text>

      {/* Obtuse Triangle */}
      <Polygon
        points={[
          [3, 2],
          [5.5, 2],
          [3.3, 2.8],
        ]}
        color="#ec4899"
        fillOpacity={0.3}
        strokeWidth={2}
      />
      <Text x={4} y={1.2} color="#db2777" size={14} weight="bold">
        Obtuse
      </Text>
      <Text x={3.5} y={0.9} color="#1f2937" size={12}>
        One angle &gt; 90°
      </Text>
    </Mafs>
  </div>
);

const SpecialTrianglesVisual = () => (
  <div
    style={{
      padding: '1rem',
      background: 'white',
      borderRadius: '8px',
      border: '1px solid var(--liwaza-outline-variant)',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1rem',
        color: 'var(--liwaza-primary)',
      }}
    >
      Special Right Triangles
    </h4>
    <Mafs width={400} height={300} viewBox={{ x: [-5, 5], y: [-3, 3] }}>
      <Coordinates.Cartesian />

      {/* 45-45-90 Triangle */}
      <Polygon
        points={[
          [-3, -1],
          [-1, -1],
          [-1, 1],
        ]}
        color="#2563eb"
        fillOpacity={0.3}
        strokeWidth={2}
      />
      <Text x={-2.5} y={-1.5} color="#1d4ed8" size={14} weight="bold">
        45-45-90
      </Text>
      <Text x={-2.5} y={-2} color="#1f2937" size={12}>
        1 : 1 : √2
      </Text>

      {/* 30-60-90 Triangle */}
      <Polygon
        points={[
          [1, -1],
          [3, -1],
          [3, 0.73],
        ]}
        color="#10b981"
        fillOpacity={0.3}
        strokeWidth={2}
      />
      <Text x={1.5} y={-1.5} color="#059669" size={14} weight="bold">
        30-60-90
      </Text>
      <Text x={1.3} y={-2} color="#1f2937" size={12}>
        1 : √3 : 2
      </Text>
    </Mafs>
  </div>
);

const PythagoreanVisual = () => (
  <div
    style={{
      padding: '1rem',
      background: 'white',
      borderRadius: '8px',
      border: '1px solid var(--liwaza-outline-variant)',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1rem',
        color: 'var(--liwaza-primary)',
      }}
    >
      Pythagorean Theorem
    </h4>
    <Mafs width={400} height={300} viewBox={{ x: [-2, 4], y: [-1, 3] }}>
      <Coordinates.Cartesian />

      {/* Right triangle */}
      <Polygon
        points={[
          [0, 0],
          [3, 0],
          [0, 2],
        ]}
        color="#2563eb"
        fillOpacity={0.3}
        strokeWidth={2}
      />

      {/* Right angle indicator */}
      <Polygon
        points={[
          [0.2, 0],
          [0.2, 0.2],
          [0, 0.2],
        ]}
        color="#ef4444"
        strokeWidth={1}
        fillOpacity={0}
      />

      {/* Side labels */}
      <Text x={1.5} y={-0.3} color="#059669" size={14} weight="bold">
        a
      </Text>
      <Text x={-0.5} y={1} color="#059669" size={14} weight="bold">
        b
      </Text>
      <Text x={1.8} y={1.3} color="#dc2626" size={14} weight="bold">
        c
      </Text>

      {/* Formula */}
      <Text x={0} y={2.5} color="#1f2937" size={16} weight="bold">
        a² + b² = c²
      </Text>
    </Mafs>
  </div>
);

const TriangleAreaVisual = () => (
  <div
    style={{
      padding: '1rem',
      background: 'white',
      borderRadius: '8px',
      border: '1px solid var(--liwaza-outline-variant)',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1rem',
        color: 'var(--liwaza-primary)',
      }}
    >
      Triangle Area Calculation
    </h4>
    <Mafs width={400} height={300} viewBox={{ x: [-1, 8], y: [-1, 6] }}>
      <Coordinates.Cartesian
        xAxis={{ lines: false, labels: false }}
        yAxis={{ lines: false, labels: false }}
      />

      {/* Triangle */}
      <Polygon
        points={[
          [1, 1],
          [7, 1],
          [4, 5],
        ]}
        color="#2563eb"
        fillOpacity={0.3}
        strokeWidth={3}
      />

      {/* Base line */}
      <Plot.OfX y={() => 1} color="#10b981" strokeWidth={3} domain={[1, 7]} />
      <Text x={4} y={0.5} color="#059669" size={14} weight="bold">
        base = 6
      </Text>

      {/* Height line (dashed) */}
      <Plot.OfY
        x={() => 4}
        color="#ef4444"
        strokeWidth={3}
        strokeDasharray="5,5"
        domain={[1, 5]}
      />
      <Text x={4.3} y={3} color="#dc2626" size={14} weight="bold">
        height = 4
      </Text>

      {/* Right angle indicator */}
      <Polygon
        points={[
          [3.8, 1],
          [4.2, 1],
          [4.2, 1.4],
          [3.8, 1.4],
        ]}
        color="#ef4444"
        strokeWidth={1}
        fillOpacity={0}
      />

      {/* Formula */}
      <Text x={1} y={5.5} color="#1f2937" size={14} weight="bold">
        Area Formula:
      </Text>
      <Text x={1} y={5.1} color="#1d4ed8" size={13} weight="bold">
        A = ½ × base × height
      </Text>
      <Text x={1} y={4.7} color="#1f2937" size={12}>
        A = ½ × 6 × 4 = 12
      </Text>
    </Mafs>
  </div>
);

const CongruenceSimilarityVisual = () => (
  <div
    style={{
      padding: '1rem',
      background: 'white',
      borderRadius: '8px',
      border: '1px solid var(--liwaza-outline-variant)',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1rem',
        color: 'var(--liwaza-primary)',
      }}
    >
      Congruent vs Similar Triangles
    </h4>
    <Mafs width={500} height={300} viewBox={{ x: [-1, 10], y: [-1, 6] }}>
      <Coordinates.Cartesian
        xAxis={{ lines: false, labels: false }}
        yAxis={{ lines: false, labels: false }}
      />

      <Text x={1.5} y={5.5} color="#1d4ed8" size={14} weight="bold">
        Congruent Triangles
      </Text>
      <Text x={1} y={5.1} color="#1f2937" size={12}>
        Same size & shape
      </Text>

      {/* First congruent triangle */}
      <Polygon
        points={[
          [1, 2],
          [2.5, 2],
          [1.75, 3.5],
        ]}
        color="#2563eb"
        fillOpacity={0.3}
        strokeWidth={2}
      />

      {/* Second congruent triangle */}
      <Polygon
        points={[
          [3, 2],
          [4.5, 2],
          [3.75, 3.5],
        ]}
        color="#2563eb"
        fillOpacity={0.3}
        strokeWidth={2}
      />

      {/* Labels for congruent */}
      <Text x={1.75} y={1.5} color="#059669" size={12} weight="bold">
        6
      </Text>
      <Text x={3.75} y={1.5} color="#059669" size={12} weight="bold">
        6
      </Text>

      <Text x={2.5} y={4} color="#1d4ed8" size={18} weight="bold">
        ≅
      </Text>
      <Text x={1.5} y={1} color="#1f2937" size={12}>
        All sides equal
      </Text>
      <Text x={1.5} y={0.7} color="#1f2937" size={12}>
        All angles equal
      </Text>

      {/* Similar triangles */}
      <Text x={6.5} y={5.5} color="#dc2626" size={14} weight="bold">
        Similar Triangles
      </Text>
      <Text x={6.5} y={5.1} color="#1f2937" size={12}>
        Same shape, different size
      </Text>

      {/* First similar triangle (larger) */}
      <Polygon
        points={[
          [6, 2],
          [8, 2],
          [7, 4],
        ]}
        color="#ef4444"
        fillOpacity={0.3}
        strokeWidth={2}
      />

      {/* Second similar triangle (smaller) */}
      <Polygon
        points={[
          [6.5, 3.5],
          [7.5, 3.5],
          [7, 4.5],
        ]}
        color="#ef4444"
        fillOpacity={0.3}
        strokeWidth={2}
      />

      {/* Labels for similar */}
      <Text x={7} y={1.5} color="#059669" size={12} weight="bold">
        12
      </Text>
      <Text x={7} y={3.2} color="#059669" size={12} weight="bold">
        6
      </Text>

      <Text x={7.7} y={3.75} color="#dc2626" size={18} weight="bold">
        ∼
      </Text>
      <Text x={6.5} y={1} color="#1f2937" size={12}>
        Ratio 2:1
      </Text>
      <Text x={6.5} y={0.7} color="#1f2937" size={12}>
        Same angles
      </Text>
    </Mafs>
  </div>
);

const TrianglesLesson = () => {
  const sections = [
    {
      id: 'triangle-basics',
      title: 'Triangle Basics',
      content: {
        introduction:
          'Triangles are three-sided polygons with fundamental properties that appear throughout geometry. Understanding basic triangle relationships is essential for solving many GRE problems.',

        concepts: [
          {
            title: 'Triangle Sum Property',
            explanation:
              'The sum of all interior angles in any triangle equals 180°. This is one of the most important properties in triangle geometry.',
            visual: <TriangleBasicsVisual />,
            example: {
              problem:
                'In triangle ABC, angle A = 50° and angle B = 50°. Find angle C',
              solution: [
                'Use angle sum property: §∠A + ∠B + ∠C = 180°§',
                'Substitute known values: §50° + 50° + ∠C = 180°§',
                'Simplify: §100° + ∠C = 180°§',
                'Therefore: §∠C = 180° - 100° = 80°§',
              ],
            },
          },
          {
            title: 'Triangle Classification',
            explanation:
              'Triangles are classified by sides (equilateral, isosceles, scalene) and by angles (right, acute, obtuse).',
            visual: <TriangleTypesVisual />,
            example: {
              problem:
                'A triangle has sides of length 5, 5, and 8. Classify the triangle',
              solution: [
                'Check side lengths: 5, 5, 8',
                'Two sides have equal length (5 = 5)',
                'The third side (8) is different',
                'This is an isosceles triangle',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'special-triangles',
      title: 'Special Triangles',
      content: {
        introduction:
          'Special right triangles have fixed angle and side relationships that allow for quick calculations. The 45-45-90 and 30-60-90 triangles are particularly important for the GRE.',

        concepts: [
          {
            title: 'Pythagorean Theorem',
            explanation:
              'In any right triangle, §a² + b² = c²§, where c is the hypotenuse. This fundamental relationship connects the sides of right triangles.',
            visual: <PythagoreanVisual />,
            example: {
              problem:
                'A right triangle has legs of length 9 and 12. Find the hypotenuse',
              solution: [
                'Use Pythagorean theorem: §a² + b² = c²§',
                'Substitute: §9² + 12² = c²§',
                'Calculate: §81 + 144 = c²§',
                'Therefore: §c² = 225§, so §c = 15§',
              ],
            },
          },
          {
            title: '45-45-90 and 30-60-90 Triangles',
            explanation:
              'In a 45-45-90 triangle, sides are in ratio §1:1:\\sqrt{2}§. In a 30-60-90 triangle, sides are in ratio §1:\\sqrt{3}:2§.',
            visual: <SpecialTrianglesVisual />,
            example: {
              problem:
                'In a 45-45-90 triangle, one leg has length 6. Find the other sides',
              solution: [
                'In 45-45-90 triangle, ratio is §1:1:\\sqrt{2}§',
                'Both legs are equal, so other leg = 6',
                'Hypotenuse = §\\text{leg} \\times \\sqrt{2} = 6\\sqrt{2}§',
                'Sides are: 6, 6, §6\\sqrt{2}§',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'triangle-area',
      title: 'Triangle Area',
      content: {
        introduction:
          'Triangle area can be calculated using various formulas depending on the given information. The most common is the base-height formula.',

        concepts: [
          {
            title: 'Base-Height Formula',
            explanation:
              '§Area = ½ \\times \\text{base} \\times \\text{height}§. The height must be perpendicular to the base. Any side can serve as the base.',
            visual: <TriangleAreaVisual />,
            example: {
              problem:
                'A triangle has a base of 14 and a height of 8. Find its area',
              solution: [
                'Use formula: §Area = ½ \\times \\text{base} \\times \\text{height}§',
                'Substitute values: §Area = ½ \\times 14 \\times 8§',
                'Calculate: §Area = ½ \\times 112 = 56§',
                'The area is 56 square units',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'congruence-similarity',
      title: 'Congruence & Similarity',
      content: {
        introduction:
          'Congruent triangles have identical size and shape, while similar triangles have the same shape but different sizes. Understanding these relationships helps solve many geometric problems.',

        concepts: [
          {
            title: 'Triangle Congruence and Similarity',
            explanation:
              'Triangles are congruent if they have the same size and shape. Triangles are similar if corresponding angles are equal (and sides are proportional).',
            visual: <CongruenceSimilarityVisual />,
            example: {
              problem:
                "Triangle ABC ~ Triangle DEF with sides in ratio 3:2. If ABC has perimeter 21, find DEF's perimeter",
              solution: [
                'Similar triangles have proportional sides',
                'Ratio of sides = 3:2',
                'Ratio of perimeters = ratio of corresponding sides',
                'Perimeter of DEF = §(2/3) \\times 21 = 14§',
              ],
            },
          },
        ],
      },
    },
  ];

  const practiceQuestions = [
    {
      type: 'timed',
      timeLimit: 90,
      question:
        'In triangle ABC, angle A = 50° and angle B = 75°. What is angle C?',
      options: ['55°', '65°', '60°', '70°', '45°'],
      correct: 0,
      explanation:
        'Sum of angles in a triangle = 180°. So §C = 180° - 50° - 75° = 55°§',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question:
        'In a 45-45-90 triangle, if one leg is 8, what is the hypotenuse?',
      options: ['§8\\sqrt{2}§', '§8\\sqrt{3}§', '16', '12', '§4\\sqrt{2}§'],
      correct: 0,
      explanation:
        'In a 45-45-90 triangle, hypotenuse = §\\text{leg} \\times \\sqrt{2} = 8\\sqrt{2}§',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question: 'A triangle has base 10 and height 6. What is its area?',
      options: ['30', '60', '16', '20', '15'],
      correct: 0,
      explanation:
        'Area = §(1/2) \\times \\text{base} \\times \\text{height} = (1/2) \\times 10 \\times 6 = 30§',
    },
    {
      type: 'timed',
      timeLimit: 150,
      question: 'Can sides of lengths 2, 5, and 8 form a triangle?',
      options: [
        'No',
        'Yes',
        "Only if it's a right triangle",
        "Only if it's isosceles",
        'Cannot determine',
      ],
      correct: 0,
      explanation:
        'Check triangle inequality: §2 + 5 = 7§, which is NOT > 8. Therefore, these cannot form a triangle.',
    },
    {
      type: 'timed',
      timeLimit: 110,
      question:
        'In a 30-60-90 triangle, if the side opposite 30° is 4, what is the hypotenuse?',
      options: ['8', '§4\\sqrt{3}§', '§4\\sqrt{2}§', '12', '6'],
      correct: 0,
      explanation:
        'In 30-60-90 triangle, if shortest side is x, hypotenuse is 2x. So hypotenuse = §2 \\times 4 = 8§',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question:
        'A right triangle has legs of 21 and 28. What is the hypotenuse?',
      options: ['35', '42', '49', '30', '45'],
      correct: 0,
      explanation:
        'This is a multiple of the 3-4-5 triangle: §21 = 3 \\times 7§, §28 = 4 \\times 7§, so hypotenuse = §5 \\times 7 = 35§.',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question: 'A square has diagonal 14. What is the side length?',
      options: ['§7\\sqrt{2}§', '§14\\sqrt{2}§', '7', '§7\\sqrt{3}§', '28'],
      correct: 0,
      explanation:
        'In a 45-45-90 triangle (half of square), diagonal = §\\text{side} \\times \\sqrt{2}§. So side = §14/\\sqrt{2} = 7\\sqrt{2}§.',
    },
    {
      type: 'timed',
      question:
        'What type of triangle has all three sides of different lengths?',
      options: ['Scalene', 'Isosceles', 'Equilateral', 'Right', 'Obtuse'],
      correct: 0,
      explanation:
        'A scalene triangle has all three sides of different lengths.',
    },
    {
      type: 'timed',
      question: 'A right triangle has legs of 3 and 4. What is the hypotenuse?',
      options: ['5', '7', '6', '§\\sqrt{7}§', '§\\sqrt{12}§'],
      correct: 0,
      explanation:
        'Using Pythagorean theorem: §c² = 3² + 4² = 9 + 16 = 25§, so c = 5. This is the 3-4-5 Pythagorean triple.',
    },
    {
      type: 'timed',
      question:
        'Two similar triangles have corresponding sides in the ratio 2:5. If the smaller triangle has perimeter 14, what is the perimeter of the larger triangle?',
      options: ['35', '28', '20', '42', '30'],
      correct: 0,
      explanation:
        'Perimeters of similar triangles are in the same ratio as corresponding sides. So §14:P = 2:5§, which gives §P = 35§.',
    },
    {
      type: 'timed',
      question: 'In an equilateral triangle, each angle measures:',
      options: ['60°', '90°', '45°', '120°', '30°'],
      correct: 0,
      explanation:
        'In an equilateral triangle, all angles are equal. Since the sum is 180°, each angle = §180°/3 = 60°§.',
    },
    {
      type: 'timed',
      question: 'Triangle with sides 5, 12, 13 is:',
      options: [
        'Right triangle',
        'Isosceles triangle',
        'Equilateral triangle',
        'Obtuse triangle',
        'Cannot exist',
      ],
      correct: 0,
      explanation:
        'Check: §5² + 12² = 25 + 144 = 169 = 13²§. Since §a² + b² = c²§, this is a right triangle (5-12-13 Pythagorean triple).',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question:
        'An equilateral triangle has side length 8. What is its height?',
      options: ['§4\\sqrt{3}§', '§8\\sqrt{3}§', '§4\\sqrt{2}§', '6', '8'],
      correct: 0,
      explanation:
        'In an equilateral triangle, height = §(\\sqrt{3}/2) \\times \\text{side} = (\\sqrt{3}/2) \\times 8 = 4\\sqrt{3}§.',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question: 'Which of these is NOT a Pythagorean triple?',
      options: ['6-8-12', '7-24-25', '8-15-17', '9-12-15', '20-21-29'],
      correct: 0,
      explanation:
        'Check: §6² + 8² = 36 + 64 = 100§, but §12² = 144§. Since 100 ≠ 144, this is not a Pythagorean triple.',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question:
        'An equilateral triangle has a side length of 6 cm. What is its area?',
      options: [
        '§9\\sqrt{3}§ cm²',
        '§18\\sqrt{3}§ cm²',
        '§12\\sqrt{3}§ cm²',
        '§6\\sqrt{3}§ cm²',
        '§15\\sqrt{3}§ cm²',
      ],
      correct: 0,
      explanation:
        'Area of equilateral triangle = §\\frac{s^2\\sqrt{3}}{4} = \\frac{6^2\\sqrt{3}}{4} = \\frac{36\\sqrt{3}}{4} = 9\\sqrt{3}§ cm².',
    },

    {
      type: 'untimed',
      timeLimit: 90,
      question:
        'In triangle ABC, angle A = 50° and angle B = 75°. What is angle C?',
      options: ['55°', '65°', '60°', '70°', '45°'],
      correct: 0,
      explanation:
        'Sum of angles in a triangle = 180°. So §C = 180° - 50° - 75° = 55°§',
    },
    {
      type: 'untimed',
      timeLimit: 120,
      question:
        'In a 45-45-90 triangle, if one leg is 8, what is the hypotenuse?',
      options: ['§8\\sqrt{2}§', '§8\\sqrt{3}§', '16', '12', '§4\\sqrt{2}§'],
      correct: 0,
      explanation:
        'In a 45-45-90 triangle, hypotenuse = §\\text{leg} \\times \\sqrt{2} = 8\\sqrt{2}§',
    },
    {
      type: 'untimed',
      timeLimit: 100,
      question: 'A triangle has base 10 and height 6. What is its area?',
      options: ['30', '60', '16', '20', '15'],
      correct: 0,
      explanation:
        'Area = §(1/2) \\times \\text{base} \\times \\text{height} = (1/2) \\times 10 \\times 6 = 30§',
    },
    {
      type: 'untimed',
      timeLimit: 150,
      question: 'Can sides of lengths 2, 5, and 8 form a triangle?',
      options: [
        'No',
        'Yes',
        "Only if it's a right triangle",
        "Only if it's isosceles",
        'Cannot determine',
      ],
      correct: 0,
      explanation:
        'Check triangle inequality: §2 + 5 = 7§, which is NOT > 8. Therefore, these cannot form a triangle.',
    },
    {
      type: 'untimed',
      timeLimit: 110,
      question:
        'In a 30-60-90 triangle, if the side opposite 30° is 4, what is the hypotenuse?',
      options: ['8', '§4\\sqrt{3}§', '§4\\sqrt{2}§', '12', '6'],
      correct: 0,
      explanation:
        'In 30-60-90 triangle, if shortest side is x, hypotenuse is 2x. So hypotenuse = §2 \\times 4 = 8§',
    },
    {
      type: 'untimed',
      timeLimit: 90,
      question:
        'A right triangle has legs of 21 and 28. What is the hypotenuse?',
      options: ['35', '42', '49', '30', '45'],
      correct: 0,
      explanation:
        'This is a multiple of the 3-4-5 triangle: §21 = 3 \\times 7§, §28 = 4 \\times 7§, so hypotenuse = §5 \\times 7 = 35§.',
    },
    {
      type: 'untimed',
      timeLimit: 100,
      question: 'A square has diagonal 14. What is the side length?',
      options: ['§7\\sqrt{2}§', '§14\\sqrt{2}§', '7', '§7\\sqrt{3}§', '28'],
      correct: 0,
      explanation:
        'In a 45-45-90 triangle (half of square), diagonal = §\\text{side} \\times \\sqrt{2}§. So side = §14/\\sqrt{2} = 7\\sqrt{2}§.',
    },
    {
      type: 'untimed',
      question:
        'What type of triangle has all three sides of different lengths?',
      options: ['Scalene', 'Isosceles', 'Equilateral', 'Right', 'Obtuse'],
      correct: 0,
      explanation:
        'A scalene triangle has all three sides of different lengths.',
    },
    {
      type: 'untimed',
      question: 'A right triangle has legs of 3 and 4. What is the hypotenuse?',
      options: ['5', '7', '6', '§\\sqrt{7}§', '§\\sqrt{12}§'],
      correct: 0,
      explanation:
        'Using Pythagorean theorem: §c² = 3² + 4² = 9 + 16 = 25§, so c = 5. This is the 3-4-5 Pythagorean triple.',
    },
    {
      type: 'untimed',
      question:
        'Two similar triangles have corresponding sides in the ratio 2:5. If the smaller triangle has perimeter 14, what is the perimeter of the larger triangle?',
      options: ['35', '28', '20', '42', '30'],
      correct: 0,
      explanation:
        'Perimeters of similar triangles are in the same ratio as corresponding sides. So §14:P = 2:5§, which gives §P = 35§.',
    },
    {
      type: 'untimed',
      question: 'In an equilateral triangle, each angle measures:',
      options: ['60°', '90°', '45°', '120°', '30°'],
      correct: 0,
      explanation:
        'In an equilateral triangle, all angles are equal. Since the sum is 180°, each angle = §180°/3 = 60°§.',
    },
    {
      type: 'untimed',
      question: 'Triangle with sides 5, 12, 13 is:',
      options: [
        'Right triangle',
        'Isosceles triangle',
        'Equilateral triangle',
        'Obtuse triangle',
        'Cannot exist',
      ],
      correct: 0,
      explanation:
        'Check: §5² + 12² = 25 + 144 = 169 = 13²§. Since §a² + b² = c²§, this is a right triangle (5-12-13 Pythagorean triple).',
    },
    {
      type: 'untimed',
      timeLimit: 90,
      question:
        'An equilateral triangle has side length 8. What is its height?',
      options: ['§4\\sqrt{3}§', '§8\\sqrt{3}§', '§4\\sqrt{2}§', '6', '8'],
      correct: 0,
      explanation:
        'In an equilateral triangle, height = §(\\sqrt{3}/2) \\times \\text{side} = (\\sqrt{3}/2) \\times 8 = 4\\sqrt{3}§.',
    },
    {
      type: 'untimed',
      timeLimit: 90,
      question: 'Which of these is NOT a Pythagorean triple?',
      options: ['6-8-12', '7-24-25', '8-15-17', '9-12-15', '20-21-29'],
      correct: 0,
      explanation:
        'Check: §6² + 8² = 36 + 64 = 100§, but §12² = 144§. Since 100 ≠ 144, this is not a Pythagorean triple.',
    },
    {
      type: 'untimed',
      timeLimit: 90,
      question:
        'An equilateral triangle has a side length of 6 cm. What is its area?',
      options: [
        '§9\\sqrt{3}§ cm²',
        '§18\\sqrt{3}§ cm²',
        '§12\\sqrt{3}§ cm²',
        '§6\\sqrt{3}§ cm²',
        '§15\\sqrt{3}§ cm²',
      ],
      correct: 0,
      explanation:
        'Area of equilateral triangle = §\\frac{s^2\\sqrt{3}}{4} = \\frac{6^2\\sqrt{3}}{4} = \\frac{36\\sqrt{3}}{4} = 9\\sqrt{3}§ cm².',
    },
    {
      type: 'untimed',
      questionType: 'quantitative_comparison',
      question:
        'In a right triangle, the legs are 6 and 8. Compare the two quantities.',
      quantityA: 'Hypotenuse',
      quantityB: '§10§',
      correct: 2,
      explanation:
        'Compare the two quantities directly. The two quantities are equal.',
    },
    {
      type: 'untimed',
      questionType: 'multiple_choice_multi',
      question:
        'Which side lengths can form a triangle? Select all that apply.',
      options: ['§3,4,5§', '§2,3,6§', '§5,5,9§', '§1,1,3§', '§6,7,8§'],
      correct: [0, 2, 4],
      explanation:
        'A set forms a triangle when the sum of any two sides is greater than the third.',
    },
    {
      type: 'timed',
      timeLimit: 105,
      questionType: 'numeric_entry',
      question: 'A triangle has base 10 and height 6. Enter its area.',
      correctAnswer: '30',
      acceptedAnswers: ['30'],
      explanation: 'Area is §(1/2)bh = (1/2)(10)(6)=30§.',
    },
  ];

  const quickReference = [
    '§Area = (1/2) \\times \\text{base} \\times \\text{height}§',
    'Angle sum = 180°',
    '45-45-90: sides in ratio §1:1:\\sqrt{2}§',
    '30-60-90: sides in ratio §1:\\sqrt{3}:2§',
    'Pythagorean: §a² + b² = c²§',
    'Common triples: 3-4-5, 5-12-13, 8-15-17',
    'Equilateral area: §(\\sqrt{3}/4) \\times s²§',
  ];

  const getStartedWithProps = {
    title: 'Need help with Triangle concepts?',
    spaceName: 'Quant hacks',
    humanCoachTitle: 'Talk to a GRE coach',
    liwazaAITitle: 'Ask AI',
    communityTitle: 'Ask other students',
    showHumanCoach: true,
    showLiwazaAI: true,
    showCommunity: true,
    aifirstmessage:
      'I need help understanding Triangle concepts for the GRE Quantitative section',
    aicontext:
      'Master triangle properties, area formulas, special triangles, Pythagorean theorem, and similarity',
  };

  return (
    <LessonTemplate
      lessonId="triangles"
      lessonTitle="Triangles"
      lessonIcon={<FiTriangle />}
      lessonDescription="Master triangle properties, special triangles, area calculations, and similarity"
      sections={sections}
      practiceQuestions={practiceQuestions}
      quickReference={quickReference}
      getStartedWithProps={getStartedWithProps}
      supplementalPracticeSlot={
        <QuantLessonPracticeSlot lessonId="triangles" />
      }
    />
  );
};

export default TrianglesLesson;
